import { cardapioApi, CARDAPIO_TRANSLATION_BATCH_SIZE } from "./api";
import { runWithConcurrencyLimit } from "./concurrency";
import type { CardapioCatalogo, CartItem } from "./types";

export { CARDAPIO_TRANSLATION_BATCH_SIZE };

export const CARDAPIO_TRANSLATION_CONCURRENCY = 4;
export const CARDAPIO_TRANSLATION_RETRY_DELAY_MS = 400;

export type TranslationItem = {
  entity_type: "empresa" | "categoria" | "produto" | "promocao";
  entity_id: string;
  field: "nome" | "descricao" | "subtitulo";
  text: string;
};

export type DynamicTranslationMap = Record<string, string>;

const SESSION_KEY = "cardapio_dynamic_translations_v1";

export function translationKey(entityType: string, entityId: string, field: string): string {
  return `${entityType}:${entityId}:${field}`;
}

export function categoryEntityId(nome: string): string {
  const trimmed = nome.trim();
  if (trimmed.length <= 64) return trimmed;
  return trimmed.slice(0, 64);
}

export function collectTranslationItems(catalogo: CardapioCatalogo | null): TranslationItem[] {
  if (!catalogo) return [];
  const items: TranslationItem[] = [];
  const push = (
    entity_type: TranslationItem["entity_type"],
    entity_id: string,
    field: TranslationItem["field"],
    text: string | null | undefined,
  ) => {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return;
    items.push({ entity_type, entity_id, field, text: trimmed });
  };

  push("empresa", "0", "nome", catalogo.empresa.nome);
  for (const cat of catalogo.categorias) {
    push("categoria", categoryEntityId(cat.nome), "nome", cat.nome);
    for (const p of cat.produtos) {
      push("produto", String(p.id), "nome", p.nome);
      push("produto", String(p.id), "descricao", p.descricao);
    }
  }
  for (const promo of catalogo.promocoes) {
    push("promocao", String(promo.id), "nome", promo.nome);
    push("promocao", String(promo.id), "subtitulo", promo.subtitulo);
  }
  return items;
}

export function loadSessionTranslationCache(): Partial<Record<"en" | "es", DynamicTranslationMap>> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<Record<"en" | "es", DynamicTranslationMap>>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveSessionTranslationCache(
  locale: "en" | "es",
  map: DynamicTranslationMap,
): void {
  try {
    const current = loadSessionTranslationCache();
    current[locale] = map;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(current));
  } catch {
    /* ignore quota errors */
  }
}

export function mergeSessionTranslationCache(
  locale: "en" | "es",
  partial: DynamicTranslationMap,
): void {
  try {
    const current = loadSessionTranslationCache();
    current[locale] = { ...(current[locale] ?? {}), ...partial };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(current));
  } catch {
    /* ignore quota errors */
  }
}

export function chunkTranslationItems(
  items: TranslationItem[],
  batchSize = CARDAPIO_TRANSLATION_BATCH_SIZE,
): TranslationItem[][] {
  const batches: TranslationItem[][] = [];
  for (let offset = 0; offset < items.length; offset += batchSize) {
    batches.push(items.slice(offset, offset + batchSize));
  }
  return batches;
}

export function filterItemsNeedingTranslation(
  items: TranslationItem[],
  map: DynamicTranslationMap,
): TranslationItem[] {
  return items.filter((item) => {
    const key = translationKey(item.entity_type, item.entity_id, item.field);
    return !map[key];
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function traduzirBatchWithRetry(
  target: "en" | "es",
  batch: TranslationItem[],
  retryDelayMs: number,
): Promise<{ ok: boolean; translations: DynamicTranslationMap }> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { translations } = await cardapioApi.traduzirBatch({ target, items: batch });
      return { ok: true, translations: translations ?? {} };
    } catch {
      if (attempt === 0) {
        await delay(retryDelayMs);
        continue;
      }
      return { ok: false, translations: {} };
    }
  }
  return { ok: false, translations: {} };
}

export type TranslateCatalogOptions = {
  target: "en" | "es";
  items: TranslationItem[];
  initialMap?: DynamicTranslationMap;
  concurrency?: number;
  retryDelayMs?: number;
  onBatchComplete?: (batchTranslations: DynamicTranslationMap, accumulated: DynamicTranslationMap) => void;
};

export type TranslateCatalogResult = {
  translations: DynamicTranslationMap;
  failedBatchCount: number;
  failedItemCount: number;
};

export async function translateCatalogItems(
  options: TranslateCatalogOptions,
): Promise<TranslateCatalogResult> {
  const {
    target,
    items,
    initialMap = {},
    concurrency = CARDAPIO_TRANSLATION_CONCURRENCY,
    retryDelayMs = CARDAPIO_TRANSLATION_RETRY_DELAY_MS,
    onBatchComplete,
  } = options;

  if (items.length === 0) {
    return { translations: { ...initialMap }, failedBatchCount: 0, failedItemCount: 0 };
  }

  const batches = chunkTranslationItems(items);
  const accumulated: DynamicTranslationMap = { ...initialMap };
  let failedBatchCount = 0;
  let failedItemCount = 0;

  await runWithConcurrencyLimit(batches, concurrency, async (batch) => {
    const result = await traduzirBatchWithRetry(target, batch, retryDelayMs);
    if (result.ok) {
      Object.assign(accumulated, result.translations);
      onBatchComplete?.(result.translations, { ...accumulated });
      return result;
    }

    failedBatchCount += 1;
    failedItemCount += batch.length;
    return result;
  });

  return {
    translations: accumulated,
    failedBatchCount,
    failedItemCount,
  };
}

export function resolveDynamicText(
  locale: "pt" | "en" | "es",
  map: DynamicTranslationMap,
  entityType: string,
  entityId: string,
  field: string,
  original: string,
): string {
  const text = original ?? "";
  if (locale === "pt" || !text.trim()) return text;
  const key = translationKey(entityType, entityId, field);
  return map[key] ?? text;
}

export function resolveCartItemNome(
  catalogo: CardapioCatalogo | null,
  locale: "pt" | "en" | "es",
  map: DynamicTranslationMap,
  item: CartItem,
): string {
  if (locale === "pt") return item.nome;
  const promo = catalogo?.promocoes.find((p) => p.nome === item.nome);
  if (promo) {
    return resolveDynamicText(locale, map, "promocao", String(promo.id), "nome", item.nome);
  }
  return resolveDynamicText(locale, map, "produto", String(item.produto_id), "nome", item.nome);
}

export function resolveCartItemDescricao(
  catalogo: CardapioCatalogo | null,
  locale: "pt" | "en" | "es",
  map: DynamicTranslationMap,
  item: CartItem,
): string | null {
  if (!item.descricao) return null;
  if (locale === "pt") return item.descricao;
  const promo = catalogo?.promocoes.find((p) => p.nome === item.nome);
  if (promo) {
    return resolveDynamicText(locale, map, "promocao", String(promo.id), "subtitulo", item.descricao);
  }
  return resolveDynamicText(locale, map, "produto", String(item.produto_id), "descricao", item.descricao);
}

export function resolveModalItemTexts(
  catalogo: CardapioCatalogo | null,
  locale: "pt" | "en" | "es",
  map: DynamicTranslationMap,
  item: { produto_id: number; nome: string; descricao: string | null },
): { nome: string; descricao: string | null } {
  if (locale === "pt") {
    return { nome: item.nome, descricao: item.descricao };
  }
  const promo = catalogo?.promocoes.find((p) => p.produto_id === item.produto_id && p.nome === item.nome);
  if (promo) {
    return {
      nome: resolveDynamicText(locale, map, "promocao", String(promo.id), "nome", item.nome),
      descricao: item.descricao
        ? resolveDynamicText(locale, map, "promocao", String(promo.id), "subtitulo", item.descricao)
        : null,
    };
  }
  return {
    nome: resolveDynamicText(locale, map, "produto", String(item.produto_id), "nome", item.nome),
    descricao: item.descricao
      ? resolveDynamicText(locale, map, "produto", String(item.produto_id), "descricao", item.descricao)
      : null,
  };
}
